import { useEffect, useRef, useState } from 'react'
import { Box, Button, Container, Stack, Typography } from '@mui/material'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Logo from '../components/Logo'
import { LoadingState } from '../components/ui/States'
import { attendeeAuth } from '../services/attendeeAuth'
import { publicRegistrationService } from '../services/registrationService'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'

/**
 * Consumes a magic-link token (`/e/:slug/verify?token=...`).
 *
 * Two purposes:
 *  - default (sign-in): the API sets the session cookie and we enter the app.
 *  - `purpose=confirm`: a public self-registration email confirmation (double
 *    opt-in). We call the confirm endpoint (NO session is created) and show a
 *    "registration received — pending approval" state.
 */
export default function VerifyPage() {
  const [params] = useSearchParams()
  const { slug } = useParams()
  const navigate = useNavigate()
  const { refresh } = useAttendeeAuth()
  const [error, setError] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const ran = useRef(false)

  const isConfirm = params.get('purpose') === 'confirm'

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    const token = params.get('token')
    if (!token) {
      setError(true)
      return
    }
    const run = async () => {
      try {
        if (isConfirm) {
          await publicRegistrationService.confirm(token)
          setConfirmed(true)
          return
        }
        await attendeeAuth.verify(token)
        await refresh()
        // Optional post-sign-in destination (e.g. "/my-registration") from the invite
        // link. Only allow same-app relative sub-paths to avoid open redirects.
        const raw = params.get('redirect')
        const sub =
          raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : ''
        const home = slug ? `/e/${slug}` : '/app'
        navigate(`${home}${sub}`, { replace: true })
      } catch {
        setError(true)
      }
    }
    void run()
  }, [params, navigate, refresh, slug, isConfirm])

  if (confirmed) {
    return (
      <CenteredCard
        icon={<CheckCircleRoundedIcon />}
        tone="success"
        title="Registration received"
        body="Thanks — your email is confirmed. Our team is reviewing your registration and will email you a link to access the event app once it's approved."
      />
    )
  }

  if (!error)
    return (
      <LoadingState label={isConfirm ? 'Confirming your email…' : 'Signing you in…'} />
    )

  return (
    <CenteredCard
      icon={<ErrorOutlineRoundedIcon />}
      tone="warning"
      title={isConfirm ? "This link didn't work" : "This link didn't work"}
      body={
        isConfirm
          ? 'This confirmation link is invalid or has expired. Please register again to receive a new one.'
          : 'Sign-in links are single-use and expire after a short time. Request a new one to continue.'
      }
      action={
        <Button variant="contained" onClick={() => navigate('/')}>
          Back to sign-in
        </Button>
      }
    />
  )
}

function CenteredCard({
  icon,
  tone,
  title,
  body,
  action,
}: {
  icon: React.ReactNode
  tone: 'success' | 'warning'
  title: string
  body: string
  action?: React.ReactNode
}) {
  const toneSx =
    tone === 'success'
      ? { bgcolor: 'rgba(0,117,188,0.12)', color: 'primary.main' }
      : { bgcolor: 'rgba(228,119,32,0.12)', color: 'warning.main' }
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Container maxWidth="xs">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Logo />
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...toneSx,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {body}
            </Typography>
          </Box>
          {action}
        </Stack>
      </Container>
    </Box>
  )
}
